import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import Button from '@components/button/Button';
import Icon from '@components/icon/Icon';
import { INPUT_BASE_CLASSNAME, INPUT_FOCUS_CLASSNAME } from '@constants/common';
import TAG_COLOR_OPTIONS from '@constants/tagColors';
import useNotify from '@hooks/notify';
import useOnClickOutside from '@utils/hooks';
import { Tag } from 'types/lead-management';

import { MAX_INPUT_LENGTH, MIN_INPUT_LENGTH } from './constants';

const POPOVER_WIDTH = 353;
const GAP = 8;
const TOP_OFFSET = 64;

interface EditTagPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (tag: Tag) => void | boolean | Promise<void | boolean>;
  editingItem: Tag | null;
  showColorSelector?: boolean;
  anchorRect?: DOMRect | null; // rect of the dropdown, passed by ItemSelector
}

/**
 * Same props contract as EditTagModal, so it can be passed as
 * `EditModalComponent`. Renders as a small panel to the right of the dropdown
 * instead of a centered modal.
 */
const EditTagPopover = ({
  isOpen,
  onClose,
  onSave,
  editingItem: tag,
  showColorSelector = true,
  anchorRect
}: EditTagPopoverProps) => {
  const [name, setName] = useState(tag?.name || '');
  const [color, setColor] = useState(
    tag?.color || TAG_COLOR_OPTIONS[0].bgColor
  );
  const [isSaving, setIsSaving] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { createErrorAlert } = useNotify();

  useEffect(() => {
    if (tag) {
      setName(tag.name || '');
      setColor(tag.color || TAG_COLOR_OPTIONS[0].bgColor);
    }
  }, [tag]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  useOnClickOutside(popoverRef, () => {
    if (isOpen) onClose();
  });

  if (!isOpen) return null;

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!tag || isSaving) return;
    if (
      trimmedName.length < MIN_INPUT_LENGTH ||
      trimmedName.length > MAX_INPUT_LENGTH
    ) {
      createErrorAlert(
        `Tag name must be between ${MIN_INPUT_LENGTH} and ${MAX_INPUT_LENGTH} characters.`
      );
      return;
    }
    setIsSaving(true);
    try {
      const saved = await onSave({
        ...tag,
        name: trimmedName,
        color: showColorSelector ? color : tag.color
      });

      if (saved !== false) onClose();
    } finally {
      setIsSaving(false);
    }
  };

  // Place to the right of the dropdown; flip to the left if it would overflow.
  let style: React.CSSProperties | undefined;
  if (anchorRect) {
    const right = anchorRect.right + GAP;
    const left =
      right + POPOVER_WIDTH > window.innerWidth
        ? Math.max(GAP, anchorRect.left - POPOVER_WIDTH - GAP)
        : right;
    const top = anchorRect.top + TOP_OFFSET;
    const estimatedHeight = 260;
    const openAbove =
      top + estimatedHeight > window.innerHeight &&
      anchorRect.top > estimatedHeight;

    style = openAbove
      ? {
          bottom: window.innerHeight - anchorRect.top + GAP,
          left,
          width: POPOVER_WIDTH
        }
      : { top, left, width: POPOVER_WIDTH };
  }

  return (
    <div
      ref={popoverRef}
      data-edit-modal
      className={clsx(
        'z-[60] rounded-lg bg-theme pt-4 shadow-lg',
        anchorRect
          ? 'fixed'
          : 'fixed left-1/2 top-1/2 w-[353px] -translate-x-1/2 -translate-y-1/2'
      )}
      style={style}
    >
      <div className="flex flex-col gap-3 px-4">
        <input
          ref={inputRef}
          type="text"
          value={name}
          minLength={MIN_INPUT_LENGTH}
          maxLength={MAX_INPUT_LENGTH}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter tag name..."
          className={`w-full rounded-lg px-4 py-2.5 text-sm placeholder:text-cofeeNeutralInactive ${INPUT_BASE_CLASSNAME} ${INPUT_FOCUS_CLASSNAME}`}
        />

        {showColorSelector && (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-darkGrey">
              Choose colour
            </span>
            <div className="flex items-center gap-2">
              {TAG_COLOR_OPTIONS.map((option) => (
                <button
                  key={option.bgColor}
                  type="button"
                  aria-label={`Select ${option.name} color`}
                  className={clsx(
                    'flex size-7 cursor-pointer items-center justify-center rounded transition-all',
                    color === option.bgColor &&
                      'border-2 border-cofeeGreyBlue11'
                  )}
                  style={{ backgroundColor: option.bgColor }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setColor(option.bgColor);
                  }}
                >
                  {color === option.bgColor && (
                    <Icon name="check" className="size-4 text-cofeeGreyBlue9" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-end gap-3 border-t border-grey30 px-4 py-3">
        <Button
          variant="tertiary"
          label="Cancel"
          size="small"
          handleClick={onClose}
          className="min-w-20"
        />
        <Button
          variant="primary"
          label="Save"
          size="small"
          handleClick={handleSave}
          disabled={isSaving}
          className="min-w-20"
        />
      </div>
    </div>
  );
};

export default EditTagPopover;
